/**
 * Record ownership rules shared with the APIs:
 * - A record with no organisationOwnerId is public: anyone signed in can read
 *   it, only a platform admin can change it.
 * - Otherwise only members of the owning organisation, or a platform admin,
 *   can change it (the API returns 403 READ_ONLY).
 * - On create, the API defaults organisationOwnerId when the user has exactly
 *   one organisation, and returns 400 OWNER_ORGANISATION_REQUIRED when they
 *   have several and none is given.
 */

export const isUserSuperAdmin = (user) => user?.platformUser?.isSuperAdmin === "true";

export const isUserAdmin = (user) => user?.platformUser?.isAdmin === "true";

/** The organisations the user has a role in. */
export const getUserOrganisationIds = (user) => [
  ...new Set(
    (user?.platformUser?.roles || []).map((r) => r?.organisationId).filter(Boolean)
  ),
];

/**
 * The organisations in the backend token's organisationId claim: the ones
 * the APIs act on for this user.
 */
export const getTokenOrganisationIds = (user) => {
  if (!user?.idToken) return [];
  try {
    const parts = user.idToken.split(".");
    if (parts.length !== 3) return [];
    const rawOrgIds = JSON.parse(atob(parts[1])).organisationId;
    if (Array.isArray(rawOrgIds)) return [...new Set(rawOrgIds)];
    if (typeof rawOrgIds === "string") return [rawOrgIds];
  } catch (err) {
    console.error("Error reading organisation IDs from token:", err);
  }
  return [];
};

/** Whether the user may PUT/DELETE this record. */
export const canEditRecord = (user, record) => {
  if (isUserSuperAdmin(user)) return true;
  const owner = record?.organisationOwnerId;
  return !!owner && getUserOrganisationIds(user).includes(owner);
};

/** The user's only organisation, or null when they have none or several. */
export const getDefaultOwnerOrganisationId = (user) => {
  const ids = getUserOrganisationIds(user);
  return ids.length === 1 ? ids[0] : null;
};

/**
 * For a POST body: keeps an organisationOwnerId already set, otherwise sets
 * the user's only organisation if they have exactly one.
 */
export const withOwnerOrganisation = (body, user) => {
  if (body?.organisationOwnerId) return body;
  const ownerId = getDefaultOwnerOrganisationId(user);
  return ownerId ? { ...body, organisationOwnerId: ownerId } : body;
};

/**
 * For a POST body whose organisationOwnerId was copied or inherited from
 * another record (a copied record, a parent deal): keeps it only if the user
 * belongs to that organisation (anything else would be a 403), otherwise
 * falls back to withOwnerOrganisation.
 */
export const withInheritedOwnerOrganisation = (body, user) => {
  const { organisationOwnerId, ...rest } = body || {};
  const keepOwner = organisationOwnerId && getUserOrganisationIds(user).includes(organisationOwnerId);
  return withOwnerOrganisation(keepOwner ? { ...rest, organisationOwnerId } : rest, user);
};
